import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES, formatDate } from "@/lib/format";

const STATUS_STYLE = {
  success: "text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10",
  pending: "text-nhp-warning border-nhp-warning/40 bg-nhp-warning/10",
  failed:  "text-nhp-critical border-nhp-critical/40 bg-nhp-critical/10",
};
const PAGE_SIZE = 20;

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/transactions", {
        params: { ...(status !== "all" ? { status } : {}), q: debouncedSearch || undefined, page, page_size: PAGE_SIZE },
      });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [status, debouncedSearch, page]);

  return (
    <div>
      <PageHeader
        testId="tx-header"
        title="Transactions"
        subtitle="M-Pesa payment ledger — every attempt captured"
        actions={
          <Tabs value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <TabsList className="bg-nhp-surface border border-nhp-border rounded-none h-10 overflow-x-auto max-w-full">
              <TabsTrigger value="all" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">All</TabsTrigger>
              <TabsTrigger value="success" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Success</TabsTrigger>
              <TabsTrigger value="pending" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Pending</TabsTrigger>
              <TabsTrigger value="failed" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Failed</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-nhp-muted" />
          <Input
            data-testid="tx-search"
            placeholder="Search by phone, receipt, or package..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-nhp-surface border-nhp-border text-nhp-text rounded-none h-10 font-mono"
          />
        </div>
        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-2">Receipt</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-3">Package</div>
            <div className="col-span-2">When</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-1">Status</div>
          </div>
          <div className="divide-y divide-nhp-border">
            {items.length === 0 && <div className="p-8 text-center text-sm text-nhp-muted">No transactions.</div>}
            {items.map((t) => (
              <div key={t.id} data-testid={`tx-row-${t.id}`} className="flex flex-col gap-1.5 md:grid md:grid-cols-12 px-4 md:px-6 py-3 md:py-4 md:items-center hover:bg-nhp-bg">
                <div className="flex items-center justify-between md:contents">
                  <div className="font-mono text-sm md:col-span-2 md:text-xs">{t.mpesa_receipt || "-"}</div>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border md:hidden ${STATUS_STYLE[t.status] || ""}`}>
                    {t.status}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-nhp-muted md:contents">
                  <div className="font-mono md:col-span-2 text-nhp-text">{t.customer_phone}</div>
                  <div className="md:col-span-3 text-nhp-text">{t.package_name}</div>
                  <div className="font-mono text-xs md:col-span-2">{formatDate(t.created_at)}</div>
                  <div className="font-mono font-bold text-nhp-primary tabular-nums md:col-span-2">{KES(t.amount_kes)}</div>
                </div>
                <div className="hidden md:block md:col-span-1">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${STATUS_STYLE[t.status] || ""}`}>
                    {t.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <Pager page={page} total={total} pageSize={PAGE_SIZE} itemLabel="transactions" onChange={setPage} testId="transactions" />
        </div>
      </div>
    </div>
  );
}
